import React, { useState } from "react";
import { User } from "../types/user.types";
import { createPortalSession } from "../services/billingService";
import MfaSettings from "./MfaSettings";

/** Maps subscription_tier from API to display label. */
function subscriptionLabel(tier: string | undefined): string {
  if (!tier) return "";
  const map: Record<string, string> = {
    DAISY_LIGHT: "Daisy — Light",
    DAISY_PRO: "Daisy — Pro",
    DAISY_SCALE: "Daisy — Scale",
    ROSE_LIGHT: "Rose — Light",
    ROSE_PRO: "Rose — Pro",
    ROSE_SCALE: "Rose — Scale",
  };
  return map[tier] || tier;
}

/** Description for subscription suite (only when tier exists). */
function subscriptionDescription(tier: string | undefined): string {
  if (!tier) return "";
  return tier.startsWith("ROSE")
    ? "Voice AI"
    : "Intelligent social media management tools";
}

/** Status to "Account Status" display (e.g. ACTIVE → Active Member). */
function accountStatusLabel(status: string | undefined): string {
  if (!status) return "";
  if (status === "ACTIVE") return "Active Member";
  if (status === "INACTIVE" || status === "NOT_ACTIVE") return "Inactive";
  if (status === "PENDING") return "Pending";
  return status;
}

/** Format date for "Registered" (e.g. September 11, 2025). */
function formatRegistered(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/** Access group badge text for header (e.g. SuperAdmin from user.groups). */
function accessGroupBadge(groups: string | undefined): string {
  if (!groups) return "";
  return groups;
}

interface UserProfileCardProps {
  user: User | null;
  idToken: string | null;
}

const TERMS_URL = "https://www.daisymarketingteam.com/terms-of-service";
const PRIVACY_URL = "https://www.daisymarketingteam.com/privacy-policy";
const SUPPORT_EMAIL = "info@daisy-ai.com";

type SupportModalType = "delete" | null;

const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-zinc-500";

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  idToken,
}) => {
  const [supportModalType, setSupportModalType] = useState<SupportModalType>(
    null
  );
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const handleOpenPortal = async () => {
    if (!idToken) return;
    setPortalLoading(true);
    setPortalError(null);
    try {
      const { url } = await createPortalSession(idToken);
      window.location.href = url;
    } catch (err: any) {
      setPortalError(err.message || "Something went wrong, please try again");
      setPortalLoading(false);
    }
  };

  const displayName = user?.display_name ?? "";
  const email = user?.email ?? "";
  const status = user?.status ?? "";
  const groups = user?.groups ?? "";
  const subscriptionTier = user?.subscription_tier ?? "";
  const subscriptionProductName = user?.subscription_product_name ?? "";
  const subscriptionStatus = user?.subscription_status ?? "";
  const createdAt = user?.created_at ?? "";

  const planLabel =
    subscriptionProductName ||
    subscriptionLabel(subscriptionTier || undefined);
  const planDescription = subscriptionDescription(
    subscriptionTier || undefined
  );

  const statusLabel = accountStatusLabel(status || undefined);
  const registeredFormatted = formatRegistered(createdAt || undefined);
  const headerBadge = accessGroupBadge(groups || undefined);

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-zinc-200/90 bg-white shadow-sm">
            <span className="material-icons-outlined text-3xl text-brand-primary-light">
              account_circle
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-brand-primary">
              Profile
            </h1>
            <p className="text-sm font-medium text-zinc-500">
              Account and subscription
            </p>
          </div>
        </div>
        {headerBadge ? (
          <span className="w-fit rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-700 shadow-sm">
            {headerBadge}
          </span>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-zinc-200/90 bg-white shadow-sm">
        <div className="border-b border-zinc-100 p-6 sm:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-zinc-100 bg-zinc-50">
              <span className="material-icons-outlined text-5xl text-brand-primary-light">
                person
              </span>
            </div>
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-bold text-zinc-900">
                  {displayName}
                </h2>
                {status ? (
                  <span className="rounded-md bg-brand-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    {status}
                  </span>
                ) : null}
              </div>
              {email ? (
                <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-600">
                  <span className="material-icons-outlined text-base text-zinc-400">
                    mail
                  </span>
                  {email}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-8 p-6 sm:p-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            <div className="space-y-2">
              <p className={labelClass}>Access group</p>
              <div className="flex items-center gap-3 rounded-lg border border-zinc-200/90 bg-zinc-50/80 p-4">
                <span className="material-icons-outlined text-xl text-brand-primary-light">
                  security
                </span>
                <p className="text-base font-semibold text-zinc-900">
                  {groups || ""}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <p className={labelClass}>Account status</p>
              <div className="flex items-center gap-3 rounded-lg border border-zinc-200/90 bg-zinc-50/80 p-4">
                {status ? (
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full bg-brand-primary"
                    aria-hidden
                  />
                ) : null}
                <p className="text-base font-semibold text-zinc-900">
                  {statusLabel}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className={labelClass}>Current subscription</p>
            <div className="flex flex-col gap-6 rounded-lg border border-zinc-200/90 bg-zinc-50/50 p-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-start gap-4 sm:items-center">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-zinc-200/80 bg-white text-brand-secondary">
                  <span className="material-icons-outlined text-3xl">
                    workspace_premium
                  </span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-zinc-900">
                    {planLabel || ""}
                  </h3>
                  {planDescription ? (
                    <p className="mt-0.5 text-sm text-zinc-600">
                      {planDescription}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:items-end">
                {portalError && (
                  <p className="text-xs text-red-500">{portalError}</p>
                )}
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                  {subscriptionStatus === "ACTIVE" && (
                    <button
                      type="button"
                      onClick={handleOpenPortal}
                      disabled={portalLoading}
                      className="rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50"
                    >
                      {portalLoading ? "Redirecting…" : "Cancel subscription"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    className="rounded-lg bg-brand-secondary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90 disabled:opacity-50"
                  >
                    {portalLoading ? "Redirecting…" : "Upgrade plan"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSupportModalType("delete")}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
                  >
                    <span className="material-icons-outlined text-lg text-zinc-500">
                      close
                    </span>
                    Delete account
                  </button>
                </div>
              </div>
            </div>
          </div>

          <MfaSettings idToken={idToken} />
        </div>

        <div className="border-t border-zinc-100 bg-zinc-50/60 px-6 py-5 sm:px-8">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-200/90 bg-white text-zinc-400">
              <span className="material-icons-outlined text-xl">
                calendar_today
              </span>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Registered
              </p>
              <p className="text-base font-semibold text-zinc-900">
                {registeredFormatted}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-100 px-6 py-4 text-center text-sm text-zinc-500 sm:px-8">
          ©2025 DaisyAI, LLC. All rights reserved.{" "}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-primary-light hover:underline"
          >
            Privacy Policy
          </a>
          {" and "}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-primary-light hover:underline"
          >
            Terms of Service
          </a>
          .
        </div>
      </div>

      {supportModalType && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setSupportModalType(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="support-modal-title"
        >
          <div
            className="w-full max-w-md rounded-xl border border-zinc-200/90 bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="support-modal-title"
              className="mb-2 text-lg font-semibold text-zinc-900"
            >
              Account deletion
            </h3>
            <p className="mb-5 text-sm text-zinc-600">
              For account deletion, please contact support at{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-brand-primary-light hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <button
              type="button"
              onClick={() => setSupportModalType(null)}
              className="rounded-lg bg-brand-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-primary-hover"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileCard;
